/**
 * Sparkline — minimal SVG line chart for accuracy/activity over time.
 *
 * Values are 0..1 (or null for "no data" gaps). Null buckets break the
 * line into segments so empty days don't get connected by sloped lines
 * (which would imply an inferred value). Activity (review count) is
 * shown as faint vertical bars behind the line so the user can see the
 * volume context: a 100% accuracy day with 1 review feels different from
 * a 70% day with 50 reviews.
 */

import { type ReactNode } from "react";

export interface SparklinePoint {
  /** Identifier for keying / aria-label. Usually a date string. */
  date: string;
  /** Primary value 0..1, or null for "no data". */
  value: number | null;
  /** Optional secondary metric (e.g. review count) shown as a faint bar. */
  weight?: number;
}

export interface SparklineProps {
  points: SparklinePoint[];
  /** Viewport width in user units. Default 280. */
  width?: number;
  /** Viewport height in user units. Default 60. */
  height?: number;
  /** Extra CSS classes for the line stroke (Tailwind-friendly). */
  lineClassName?: string;
  /** Extra CSS classes for the activity bars. */
  barClassName?: string;
  ariaLabel?: string;
}

export function Sparkline({
  points,
  width = 280,
  height = 60,
  lineClassName = "stroke-accent",
  barClassName = "fill-ink/15",
  ariaLabel = "Sparkline",
}: SparklineProps): ReactNode {
  if (points.length === 0) {
    return (
      <div
        className="text-xs text-inkFaded font-mono italic"
        role="img"
        aria-label={`${ariaLabel}: no data yet`}
      >
        no data yet
      </div>
    );
  }

  const padX = 2;
  const padY = 4;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const stepX = points.length > 1 ? innerW / (points.length - 1) : 0;

  // Activity bars normalize to the max weight across the points.
  const maxWeight = Math.max(0, ...points.map((p) => p.weight ?? 0));

  // Split points into contiguous non-null segments for a polyline per segment.
  const segments: Array<Array<{ x: number; y: number }>> = [];
  let current: Array<{ x: number; y: number }> = [];
  points.forEach((p, i) => {
    if (p.value === null) {
      if (current.length > 0) {
        segments.push(current);
        current = [];
      }
      return;
    }
    const x = padX + stepX * i;
    const y = padY + innerH * (1 - p.value);
    current.push({ x, y });
  });
  if (current.length > 0) segments.push(current);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="w-full h-12"
      role="img"
      aria-label={ariaLabel}
    >
      {/* activity bars */}
      {maxWeight > 0 &&
        points.map((p, i) => {
          const w = p.weight ?? 0;
          if (w === 0) return null;
          const barH = (w / maxWeight) * innerH;
          const x = padX + stepX * i - 1.5;
          return (
            <rect
              key={`bar-${p.date}`}
              x={x}
              y={padY + innerH - barH}
              width={3}
              height={barH}
              className={barClassName}
            />
          );
        })}
      {/* line segments */}
      {segments.map((seg, i) => (
        <polyline
          key={`seg-${i}`}
          fill="none"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={lineClassName}
          points={seg.map((pt) => `${pt.x},${pt.y}`).join(" ")}
        />
      ))}
      {/* dots on each non-null point so single-day data still renders */}
      {points.map((p, i) => {
        if (p.value === null) return null;
        const cx = padX + stepX * i;
        const cy = padY + innerH * (1 - p.value);
        return (
          <circle
            key={`dot-${p.date}`}
            cx={cx}
            cy={cy}
            r={1.5}
            className={lineClassName.replace("stroke-", "fill-")}
          />
        );
      })}
    </svg>
  );
}
